/* eslint-disable no-unused-vars */
import { ELanguage } from '@microfrontends/react-components'
import { parseFromString } from '../helpers/date'
import {
    ESortableComponent,
    IAvatarResource,
    ICertificationResource,
    IProfileData,
    IProfileInfoResource,
    IProfileSuccessCollectionResponse,
    IProfileSuccessSingleResponse,
    IRecommendationResource,
    IReferenceResource,
    IResumeLevelResource,
    IResumeLevelSuggestionResource,
    ISummary,
    IUpdateCommon,
    IUpdateCommonResponse,
    IUpdatedSummary,
    IUpdatedSummaryResponse,
    IUpdateRawResponse,
    TUserContactInfoData,
    TUserContactInfoRawData,
    IAttachmentResumeParams
} from './profileTypes'
import userService from './userService'

export const BASE_PATH = process.env.NEXT_PUBLIC_MS_BASE_PATH
export const PROFILE_API_BASE_PATH = `${BASE_PATH}/api-gateway/v1.0/profile`
export const PROFILE_API_BASE_PATH_V2 = `${BASE_PATH}/api-gateway/v2.0/profile`
export const API_LINKEDIN = `${BASE_PATH}/api-gateway/v1.0/single-crawl`
export const API_LINKEDIN_STATUS = `${BASE_PATH}/api-gateway/v1.0/crawl-status`
export const API_LINKEDIN_AVAILABLE_STATUS = `${BASE_PATH}/api-gateway/v1.0/linkedin-crawler-service-status`
export const API_UPLOAD_LINKEDIN = `${BASE_PATH}/api-gateway/v1.0/linkedin-parse-pdf`
export const API_RELOAD_USER = `${process.env.NEXT_PUBLIC_VNW_JOBSEEKER_URL}/jobseekers/ajax.php?action=reload-user-ajax`
export const API_ATTACHMENT_RESUME_LINK = `${BASE_PATH}/api-gateway/v1.0/profile/resume-file/`
class ProfileService {
    // Resume Detail fields
    private _resumeDetailDataCache: IProfileData | null = null
    private readonly _resumeDetail: Promise<IProfileData>
    private _resumeDetailResolver: (data: IProfileData) => void
    private _resumeDetailDataRequested: boolean = false
    private _aiSONO: string = ''

    // Constants
    readonly RESUME_DETAIL_API: string = `${PROFILE_API_BASE_PATH}/resume-detail`
    readonly USER_CONTACT_INFO_API: string = `${PROFILE_API_BASE_PATH}/contact`
    readonly SUMMARY_API: string = `${PROFILE_API_BASE_PATH}/summary`
    readonly REFERENCE_API: string = `${PROFILE_API_BASE_PATH}/reference`
    readonly SORT_API: string = `${PROFILE_API_BASE_PATH}/update-order`
    readonly LEVEL_API: string = `${PROFILE_API_BASE_PATH}/level`
    readonly LEVEL_CALCULATE_API: string = `${PROFILE_API_BASE_PATH}/level-calculate`
    readonly AVATAR_API: string = `${PROFILE_API_BASE_PATH}/avatar`
    readonly CERTIFICATION_API: string = `${PROFILE_API_BASE_PATH}/certification`
    readonly RECOMMENDATION_API: string = `${PROFILE_API_BASE_PATH}/recommendation`
    readonly RECOMMENDATION_SORT_API: string = `${PROFILE_API_BASE_PATH}/recommendation-order`
    readonly RECOMMENDATION_TOOLTIP_API: string = `${PROFILE_API_BASE_PATH}/recommendation-tooltip`
    readonly PROFILE_INFO_API: string = `${PROFILE_API_BASE_PATH}/info`
    readonly PROFILE_INFO_API_V2: string = `${PROFILE_API_BASE_PATH_V2}/info`

    readonly LEVEL_PARAMS_MAPPING = {
        1: 'beginner',
        2: 'intermediate',
        3: 'advanced',
        4: 'completed'
    }

    constructor() {
        this._resumeDetail = new Promise((resolve) => {
            this._resumeDetailResolver = resolve
        })
    }

    private async _getResumeDetailData() {
        this._resumeDetailDataRequested = true
        const token = await userService.getAccessToken()
        const soNo = this._aiSONO
        let aiResumeReviewPath = ''
        if (soNo) {
            aiResumeReviewPath = `?soNo=${soNo}`
        }
        const request = new Request(this.RESUME_DETAIL_API + aiResumeReviewPath, {
            method: 'get',
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
        // Get data
        const response: IProfileData = await (await fetch(request)).json()

        // Cache the json data
        this._resumeDetailDataCache = response

        // Resolve the data
        this._resumeDetailResolver(response)

        // Return it
        return response
    }

    setAIResumeReview(id: string) {
        this._aiSONO = id
    }

    async getResumeDetail(): Promise<IProfileData> {
        // Return data if there is a cache
        if (this._resumeDetailDataCache) return this._resumeDetailDataCache

        // Otherwise, return the promise, we also need to fetch the data if it's not requested yet
        if (!this._resumeDetailDataRequested) this._getResumeDetailData()

        return this._resumeDetail
    }

    async createRequest(path, method = 'GET', body?, langCode?) {
        const token = await userService.getAccessToken()
        let mainBody = {
            method: method,
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept-Language': langCode
            }
        }

        if (method !== 'GET' && method !== 'HEAD') {
            mainBody = Object.assign({}, mainBody, {
                body: body
            })
        }

        return new Request(path, mainBody)
    }

    async createRequestForm(path, body?, langCode?) {
        const token = await userService.getAccessToken()
        const data = body
        const res = await fetch(path || '', {
            body: data,
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                'Accept-Language': langCode
            }
        })
        return res.json()
    }

    async updateRequestForm(path, body?, langCode?) {
        const token = await userService.getAccessToken()
        const data = body
        const res = await fetch(path || '', {
            body: data,
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${token}`,
                'Accept-Language': langCode
            }
        })
        return res.json()
    }

    async getRequestSingle<T>(path: string): Promise<T | null> {
        const request = await this.createRequest(path)
        const response = await fetch(request)

        if (response.ok) {
            const responseData: IProfileSuccessSingleResponse<T> = await response.json()

            return {
                ...responseData?.data.attributes,
                id: responseData?.data.id
            }
        }

        return null
    }

    async getRawData<T>(path: string): Promise<T | null> {
        const request = await this.createRequest(path)
        const response = await fetch(request)
        if (response.ok) {
            const responseData = await response.json()
            return responseData as T
        }
        return null
    }

    async getRequestCollection<T>(path: string): Promise<T[]> {
        const request = await this.createRequest(path)
        const response = await fetch(request)

        if (response.ok) {
            const responseData: IProfileSuccessCollectionResponse<T> = await response.json()

            const data = responseData.data
            if (Array.isArray(data)) {
                return data.map((item) => {
                    return {
                        ...item.attributes,
                        id: item.id
                    }
                })
            }

            return []
        }

        return []
    }

    async postRequest<T = any>(path: string, body: any, langCode?: string): Promise<IUpdateCommonResponse<T>> {
        const request = await this.createRequest(path, 'POST', body, langCode)
        const response = await fetch(request)
        const responseData: IUpdateRawResponse = await response.json()

        return {
            data: responseData.data,
            meta: responseData.meta,
            error: responseData.message,
            errorCode: responseData.errorCode
        }
    }

    async putRequest<T = any>(path: string, body: string, langCode?: string): Promise<IUpdateCommonResponse<T>> {
        const request = await this.createRequest(path, 'PUT', body, langCode)
        const response = await fetch(request)
        const responseData: IUpdateRawResponse = await response.json()

        return {
            data: responseData.data,
            meta: responseData.meta,
            error: responseData.message
        }
    }

    async deleteRequest<T>(path: string, body: string): Promise<IUpdateCommonResponse> {
        const request = await this.createRequest(path, 'DELETE', body)
        const response = await fetch(request)
        const responseData: IUpdateRawResponse = await response.json()

        return {
            data: responseData.data,
            meta: responseData.meta,
            error: responseData.message
        }
    }

    async getSummary(refresh: boolean = false): Promise<ISummary | null> {
        if (refresh) {
            return await this.getRequestSingle<ISummary>(this.SUMMARY_API)
        } else {
            const resumeDetail = await this.getResumeDetail()
            if (resumeDetail) {
                return {
                    ...resumeDetail.summary?.data.attributes,
                    resumeId: resumeDetail.summary?.data.id
                }
            }
        }

        return null
    }

    async getRecommendTooltip(): Promise<{ tooltip: { status: boolean } } | null> {
        return await this.getRawData(this.RECOMMENDATION_TOOLTIP_API)
    }

    async postRecommendTooltip(): Promise<IUpdateCommonResponse<{ type: string; id: number }> | null> {
        return await this.postRequest(this.RECOMMENDATION_TOOLTIP_API, JSON.stringify({}))
    }

    async postSummary(summary: string, resumeId: number): Promise<IUpdateCommon<IUpdatedSummary>> {
        const request = await this.createRequest(
            this.SUMMARY_API,
            'POST',
            JSON.stringify({
                summary: summary,
                resumeId: resumeId
            })
        )

        const response = await fetch(request)

        if (response.ok) {
            const resData: IUpdatedSummaryResponse = await response.json()

            return {
                error: false,
                data: { id: resData?.data.id }
            }
        } else if (response.status === 400) {
            const errorData: IUpdateRawResponse = await response.json()

            return {
                error: errorData.message || '',
                data: null
            }
        }

        return {
            error: 'unknown',
            data: null
        }
    }

    async getUserContactInfo(): Promise<TUserContactInfoData | null> {
        const rawData = await this.getRequestSingle<TUserContactInfoRawData>(this.USER_CONTACT_INFO_API)

        if (rawData) {
            const transformedData = {
                ...rawData,
                isSearchable: Boolean(rawData.isSearchable),
                birthday: rawData.birthday ? parseFromString(rawData.birthday) : null
            }
            return Promise.resolve(transformedData)
        }

        return null
    }

    async updateUserContactInfo(data: TUserContactInfoData): Promise<IUpdateCommonResponse> {
        const postData = {
            ...data,
            isSearchable: data.isSearchable ? 1 : 0
        }
        return this.postRequest(this.USER_CONTACT_INFO_API, JSON.stringify(postData))
    }

    async getReference(refresh: boolean = false): Promise<IReferenceResource[]> {
        if (refresh) {
            return await this.getRequestCollection<IReferenceResource>(this.REFERENCE_API)
        } else {
            const resumeDetail = await this.getResumeDetail()
            if (resumeDetail) {
                return resumeDetail.references?.data.map((item) => {
                    return {
                        ...item.attributes,
                        id: item.id
                    }
                })
            }
        }

        return []
    }

    async getRecommendation(): Promise<IRecommendationResource[]> {
        return await this.getRequestCollection<IRecommendationResource>(this.RECOMMENDATION_API)
    }

    async postReference(resumeId: number, data: Partial<IReferenceResource>) {
        const { id, ...restData } = data

        const postData = {
            ...restData,
            resumeId: resumeId,
            referenceId: data.id
        }

        return this.postRequest(this.REFERENCE_API, JSON.stringify(postData))
    }

    async postRecommendation(data: Partial<IRecommendationResource>, isNew: boolean) {
        let url = this.RECOMMENDATION_API
        if (!isNew) {
            url += `?type=sender&entryId=${data.entryId}`
            return this.putRequest(url, JSON.stringify(data))
        }
        return this.postRequest(url, JSON.stringify(data))
    }

    async deleteRecommendation(entryId: number) {
        const deleteData = {
            entryId
        }
        return this.deleteRequest(this.RECOMMENDATION_API, JSON.stringify(deleteData))
    }

    async deleteReference(resumeId: number, referenceId: number) {
        const postData = {
            resumeId: resumeId,
            referenceId: referenceId
        }

        return this.deleteRequest(this.REFERENCE_API, JSON.stringify(postData))
    }

    async sortComponent(resumeId: number, sortedIds: number[], component: ESortableComponent) {
        const postData = {
            resumeId: resumeId,
            id: sortedIds.reverse(),
            component: component
        }

        return this.postRequest(this.SORT_API, JSON.stringify(postData))
    }

    async sortRecommendations(orderIds: number[]) {
        const postData = {
            orderIds
        }

        return this.putRequest(this.RECOMMENDATION_SORT_API, JSON.stringify(postData))
    }

    async getLevel() {
        return this.getRequestSingle<IResumeLevelResource>(this.LEVEL_API)
    }

    async getLevelSuggestion(levelId: number) {
        const levelParam = this.LEVEL_PARAMS_MAPPING[levelId] || null
        if (!levelParam) {
            return Promise.resolve(null)
        }

        return this.getRequestSingle<IResumeLevelSuggestionResource>(`${this.LEVEL_CALCULATE_API}?level=${levelParam}`)
    }

    async deleteAvatar(resumeId: number) {
        const postData = {
            resumeId: resumeId
        }

        return this.deleteRequest<IAvatarResource>(this.AVATAR_API, JSON.stringify(postData))
    }

    async getCertification(refresh: boolean = false): Promise<ICertificationResource[]> {
        if (refresh) {
            return (
                await this.getRequestCollection<ICertificationResource & { id: number }>(this.CERTIFICATION_API)
            ).map((item) => ({ ...item, certificationId: item.id }))
        } else {
            const resumeDetail = await this.getResumeDetail()
            if (resumeDetail) {
                return resumeDetail.certifications?.data.map((item) => {
                    return {
                        ...item.attributes,
                        certificationId: item.id
                    }
                })
            }
        }

        return []
    }

    async postCertification(data: ICertificationResource) {
        return this.postRequest<{ type: string; id: number; errorCode?: string; error?: string }>(
            this.CERTIFICATION_API,
            JSON.stringify(data)
        )
    }

    async deleteCertification(id: number) {
        return this.deleteRequest<ICertificationResource>(
            this.CERTIFICATION_API,
            JSON.stringify({
                certificationId: id
            })
        )
    }

    async postProfileInfo(data: Partial<IProfileInfoResource>, langCode: ELanguage) {
        data.isSearchable = data.isSearchable ? 1 : 0
        return this.postRequest(this.PROFILE_INFO_API_V2, JSON.stringify(data), langCode)
    }

    async attachmentResumeLink(params: IAttachmentResumeParams) {
        try {
            const { resumeId, accessToken } = params
            if (!accessToken) {
                return
            }

            const requestOptions: {
                method: string
                headers: any
            } = {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            }

            const response = await fetch(`${API_ATTACHMENT_RESUME_LINK}${resumeId}`, requestOptions)
            let fileLink = ''
            if (response?.status === 200) {
                const blob = await response.blob()
                fileLink = URL.createObjectURL(blob)
            }
            return fileLink
        } catch (ex) {
            console.error('Error when get file link', ex)
        }
    }
}

const profileService = new ProfileService()
export default profileService
